import { useEffect } from "react";
import { Toaster } from "sonner";
import { RouterProvider } from "react-router-dom";
import { AlertCircle } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "src/@/components/ui/alert";
import { useAppSelector } from "src/redux/hooks";
import { getUser } from "src/redux/selectors";
import SignIn from "#sign-in";
import { useFirebaseAuth } from "#hooks/initial/use-firebase-auth";
import { useHideIntercomLauncher } from "#hooks/initial/use-hide-intercom-launcher";
import { useMFA } from "#hooks/initial/use-mfa";
import { useQueryPageAccess } from "#hooks/initial/use-query-page-access";
import { useIdentify } from "#hooks/initial/use-identify";
import { router } from "#navigation/router-dom";
import { pathBeginsWithRoute } from "#app/helpers";
import { Routes } from "#navigation/routes";
import { Component as AcceptInvite } from "#accept-invite";

import { GlobalProviders } from "./providers/global-providers";

/**
 * This function will produce one of 2 outcomes:
 * 1. If user hasn't authed in a while they will be prompted for MFA
 * 2. If they pass the first check they will see our normal <Navigation>
 */

// TODO: clean this up. this is the remaining scattered logic that has accumulated over time
export const DetermineNavigation = () => {
    const authBootstrap = useFirebaseAuth();
    const { setHasVerifiedEmail, requireEmailVerification } = useMFA();
    const user = useAppSelector(getUser);

    // remove loading spinner when we ready to render the app
    useEffect(() => {
        if (authBootstrap.status !== "loading") {
            document.getElementById("rai-preloader-container")?.remove();
            window.PartnerAppJsApi?.signalReady();
        }
    }, [authBootstrap.status]);

    // special exception for invite acceptance - doesn't load the rest of our application
    const path = window.location.pathname;
    if (pathBeginsWithRoute(path, Routes.NewUserAcceptInvite)) {
        const nonce = path.slice(Routes.NewUserAcceptInvite.length + 1);
        return <AcceptInvite nonce={nonce} />;
    }

    if (authBootstrap.status === "loading") {
        return null;
    }

    if (authBootstrap.status === "signedOut") {
        return (
            <SignIn
                showEmailVerification={requireEmailVerification}
                setHasVerifiedEmail={setHasVerifiedEmail}
            />
        );
    }

    if (authBootstrap.status === "error" || !user) {
        return (
            <div className="mx-auto flex min-h-screen max-w-2xl items-center px-6">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>
                        Unable to load your Snackpass account
                    </AlertTitle>
                    <AlertDescription>
                        Your Firebase session is valid, but the restaurant
                        account lookup failed. Retry shortly or contact
                        Snackpass support.
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    if (requireEmailVerification) {
        return (
            <SignIn
                showEmailVerification
                setHasVerifiedEmail={setHasVerifiedEmail}
            />
        );
    }

    return <RouterProvider router={router} />;
};

const Hooks = () => {
    useHideIntercomLauncher();
    useIdentify();
    useQueryPageAccess();

    return null;
};

const App = () => (
    <GlobalProviders>
        <Toaster />
        <Hooks />
        <DetermineNavigation />
    </GlobalProviders>
);

export default App;
